import React, { useMemo, useState } from 'react';

import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import { getCountryObjs } from '@luxuryescapes/lib-countries';

import GridPagination from '~/components/Common/Elements/GridPagination';
import PinnedOffersForm from '~/components/PinnedOffers/PinnedOffersForm';

import { PinnedOffer } from '~/services/PublicOfferFeedService';

import dateFormatter from '~/utils/dateFormatter';

const sharedOpts: Partial<GridColDef> = {
  editable: false,
  sortable: false,
  filterable: false,
  hideable: false,
  disableColumnMenu: true,
  flex: 0.5,
};

const getCountryOptions = () => {
  const countries = getCountryObjs();
  return Object.values(countries)
    .map((country) => ({
      code: country.iso2,
      label: `${country.countryName} (${country.iso2})`,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

const countryOptions = getCountryOptions();

const getColumns = ({
  onEdit,
  onDelete,
}: {
  onEdit: (row: PinnedOffer) => void;
  onDelete: (row: PinnedOffer) => void;
}): Array<GridColDef> => [
  {
    field: 'offer_id',
    headerName: 'Offer ID',
    ...sharedOpts,
    renderCell: (params) => (
      <Typography>
        <Link href={`${window.configs['LUX_CUSTOMER_PORTAL']}/partner/offer_slug/${params.row.offer_id}`}>
          {params.row.offer_id}
        </Link>
      </Typography>
    ),
  },
  {
    field: 'offer_country',
    headerName: 'Offer Country',
    ...sharedOpts,
    renderCell: (params) => {
      const countryOption = countryOptions.find((option) => option.code === params.row.offer_country);
      return <Typography>{countryOption?.label || params.row.offer_country}</Typography>;
    },
  },
  {
    field: 'details',
    headerName: 'Details',
    ...sharedOpts,
    renderCell: (params) => (
      <Stack>
        <Typography>Min Length of Stay: {params.row.min_length_of_stay || 'N/A'}</Typography>
        <Typography>Latitude: {params.row.offer_latitude || 'N/A'}</Typography>
        <Typography>Longitude: {params.row.offer_longitude || 'N/A'}</Typography>
      </Stack>
    ),
  },
  {
    field: 'created_at',
    headerName: 'Created At',
    ...sharedOpts,
    renderCell: (params) => <Typography>{dateFormatter(params.row.created_at)}</Typography>,
  },
  {
    field: 'updated_at',
    headerName: 'Updated At',
    ...sharedOpts,
    renderCell: (params) => <Typography>{dateFormatter(params.row.updated_at)}</Typography>,
  },
  {
    field: 'actions',
    headerName: 'Actions',
    ...sharedOpts,
    renderCell: (params) => (
      <Stack direction="row" spacing={1}>
        <Button variant="outlined" size="small" onClick={() => onEdit(params.row)}>
          Edit
        </Button>
        <Button variant="outlined" size="small" onClick={() => onDelete(params.row)}>
          Delete
        </Button>
      </Stack>
    ),
  },
];

type Props = {
  offerCountry: string;
  setOfferCountry: (value: string) => void;
  offerId: string;
  setOfferId: (value: string) => void;

  data: Array<PinnedOffer>;
  isLoading: boolean;
  totalCount: number;
  pageNum: number;
  pageSize: number;
  onPageChange: (page: number, pageSize: number) => void;

  handleOpen: () => void;
  handleEditOffer: (offer: PinnedOffer) => void;
  handleDeleteOffer: (offer: PinnedOffer) => void;

  open: boolean;
  setOpen: (open: boolean) => void;
  editableOffer: PinnedOffer | null;
  fetchData: () => void;
};

export default function PinnedOffersTab({
  offerCountry,
  setOfferCountry,
  offerId,
  setOfferId,
  data,
  isLoading,
  totalCount,
  pageNum,
  pageSize,
  onPageChange,
  handleOpen,
  handleEditOffer,
  handleDeleteOffer,
  open,
  setOpen,
  editableOffer,
  fetchData,
}: Props) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [offerToDelete, setOfferToDelete] = useState<PinnedOffer | undefined>(undefined);

  // Find the selected country option based on the region prop
  const selectedCountryOption = countryOptions.find((option) => option.code === offerCountry) || null;

  const rows = useMemo(() => data.map((row, index) => ({ id: index, ...row })), [data]);

  const handleDeleteClick = (offer: PinnedOffer) => {
    setOfferToDelete(offer);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (offerToDelete) {
      handleDeleteOffer(offerToDelete);
      setDeleteDialogOpen(false);
      setOfferToDelete(undefined);
    }
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setOfferToDelete(undefined);
  };

  return (
    <>
      <Grid container spacing={2} justifyContent="flex-end" alignItems="end">
        <Grid size={8} container spacing={2}>
          <Grid size={6}>
            <Autocomplete
              id="offer_country"
              options={countryOptions}
              getOptionLabel={(option) => option.label}
              value={selectedCountryOption}
              onChange={(_, newValue) => {
                setOfferCountry(newValue?.code || '');
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Offer Country"
                  variant="outlined"
                  placeholder="Search countries..."
                  fullWidth
                />
              )}
              isOptionEqualToValue={(option, value) => option.code === value.code}
              renderOption={(props, option) => (
                <li {...props} key={option.code}>
                  {option.label}
                </li>
              )}
            />
          </Grid>
          <Grid size={6}>
            <TextField
              id="offer_id"
              label="Offer ID"
              variant="outlined"
              value={offerId}
              onChange={(e) => setOfferId(e.target.value)}
              fullWidth
              placeholder="Enter offer ID..."
            />
          </Grid>
        </Grid>
        <Grid size={4}>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button variant="contained" color="primary" onClick={handleOpen}>
              New Pinned Offer
            </Button>
          </Stack>
        </Grid>
      </Grid>

      <PinnedOffersForm open={open} setOpen={setOpen} fetchData={fetchData} editableOffer={editableOffer} />

      <Dialog open={deleteDialogOpen} onClose={cancelDelete}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the pinned offer for Offer ID: {offerToDelete?.offer_id}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete} variant="text">
            Cancel
          </Button>
          <Button onClick={confirmDelete} variant="contained" color="primary">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Box mt={2}>
        <DataGrid
          slots={{ pagination: GridPagination }}
          onPaginationModelChange={({ page, pageSize }) => onPageChange(page, pageSize)}
          loading={isLoading}
          rows={rows}
          columns={getColumns({ onEdit: handleEditOffer, onDelete: handleDeleteClick })}
          autoHeight
          rowHeight={100}
          rowCount={totalCount}
          paginationMode="server"
          pageSizeOptions={[20, 50, 100]}
          paginationModel={{ page: pageNum, pageSize: pageSize }}
        />
      </Box>
    </>
  );
}
